import { useState } from "react";
import { AlertCircle, Check, Loader2, ShieldCheck } from "lucide-react";
import type { VerifiedEvent } from "nostr-tools/pure";
import { Field, FieldGroup } from "@/components/HashDisplay";
import { blobUrl, fetchBlob } from "@/hashtree/blossom";
import { bytesEqual, fromHex, toHex } from "@/hashtree/bytes";
import { chkBlossomUri, decryptCHK } from "@/hashtree/chk";
import { decodeNode } from "@/hashtree/msgpack";
import { encodeNhash, immutableHtreeUri, mutableHtreeUri } from "@/hashtree/nhash";
import type { HashtreeBuild } from "@/hashtree/useHashtreeBuild";
import { Slide, SpecNote } from "./Slide";

interface VerifyStep {
  label: string;
  detail?: string;
  ok: boolean;
}

export function DoneSlide({
  build,
  event,
  linkKey,
  onRestart,
}: {
  build: HashtreeBuild;
  event: VerifiedEvent | null;
  linkKey: Uint8Array | undefined;
  onRestart: () => void;
}) {
  const { tree, file, folderName, treeName, identity, server, visibility } = build;
  const [steps, setSteps] = useState<VerifyStep[] | null>(null);
  const [verifying, setVerifying] = useState(false);

  if (!tree || !file) return null;

  const rootHash = tree.root.encrypted.blobHash;
  const rootKey = tree.root.encrypted.chkKey;
  const nhash = encodeNhash({ rootHash, rootKey });
  const path = [folderName, file.name];

  const mutableUri = mutableHtreeUri(identity.npub, treeName.trim() || "untitled", path);
  const linkSuffix = linkKey ? `?k=${toHex(linkKey)}` : "";

  /**
   * Walk the tree from nothing but the root hash and root key, fetching every
   * blob back from the server. If this reconstructs the file, the tree is real.
   */
  const verify = async () => {
    setVerifying(true);
    const collected: VerifyStep[] = [];
    const record = (step: VerifyStep) => {
      collected.push(step);
      setSteps([...collected]);
    };

    try {
      const rootCiphertext = await fetchBlob(server, toHex(rootHash));
      record({ label: "Fetched the root blob from the server", detail: `${rootCiphertext.length} bytes`, ok: true });

      const rootPlaintext = await decryptCHK(rootCiphertext, rootKey, rootHash);
      record({ label: "Decrypted it with the root key", ok: true });

      const rootNode = decodeNode(rootPlaintext);
      const folderLink = rootNode.l.find((link) => link.n === folderName);
      if (!folderLink) throw new Error(`no entry named ${folderName} in the root`);
      record({ label: `Found the entry "${folderName}" (t=${folderLink.t})`, ok: true });

      const folderPlaintext = await decryptCHK(
        await fetchBlob(server, toHex(folderLink.h)),
        folderLink.k!,
        folderLink.h,
      );
      const folderNode = decodeNode(folderPlaintext);
      const fileLink = folderNode.l.find((link) => link.n === file.name);
      if (!fileLink) throw new Error(`no entry named ${file.name} in ${folderName}`);
      record({ label: `Found the entry "${file.name}" (t=${fileLink.t})`, ok: true });

      // Recursively resolve chunks, exactly as BUD-17 tells a reader to.
      const resolve = async (hash: Uint8Array, key: Uint8Array, type: number): Promise<Uint8Array[]> => {
        const plaintext = await decryptCHK(await fetchBlob(server, toHex(hash)), key, hash);
        if (type === 0) return [plaintext];
        const node = decodeNode(plaintext);
        const parts: Uint8Array[] = [];
        for (const link of node.l) parts.push(...(await resolve(link.h, link.k!, link.t)));
        return parts;
      };

      const parts = await resolve(fileLink.h, fileLink.k!, fileLink.t);
      const total = parts.reduce((sum, part) => sum + part.length, 0);
      const rebuilt = new Uint8Array(total);
      let offset = 0;
      for (const part of parts) {
        rebuilt.set(part, offset);
        offset += part.length;
      }
      record({
        label: `Reassembled ${parts.length} chunk${parts.length === 1 ? "" : "s"} into ${total} bytes`,
        ok: true,
      });

      const matches = bytesEqual(rebuilt, file.bytes);
      record({
        label: matches
          ? "The reconstructed file is byte-for-byte identical to the one you picked"
          : "The reconstructed file does NOT match the original",
        ok: matches,
      });
    } catch (error) {
      record({ label: "Verification failed", detail: error instanceof Error ? error.message : String(error), ok: false });
    } finally {
      setVerifying(false);
    }
  };

  return (
    <Slide
      title="Your hashtree is live"
      lede="Every blob is on a public server and the root has a name on nostr. These links resolve in any client that implements these BUDs."
    >
      <div className="space-y-2 rounded-lg border-2 border-primary/40 bg-primary/5 p-4">
        <p className="text-sm font-medium">References to your tree</p>
        <FieldGroup className="border-primary/20 bg-background/60">
          <Field
            label="mutable"
            value={`${mutableUri}${linkSuffix}`}
            hint="Resolves through your kind 30064 event, so it follows the tree as you republish it"
          />
          <Field
            label="immutable"
            value={immutableHtreeUri({ rootHash, rootKey }, path)}
            hint="Pinned to this exact root forever, key included"
          />
          <Field label="nhash" value={nhash} />
          <Field label="root hash" value={toHex(rootHash)} tone="hash" />
          <Field label="root key" value={toHex(rootKey)} tone="key" />
        </FieldGroup>
      </div>

      <FieldGroup>
        <Field label="npub" value={identity.npub} />
        <Field
          label="nsec"
          value={identity.nsec}
          tone="key"
          hint="A throwaway key made in your browser. It is the only thing that can republish this tree name — save it if you want to."
        />
        <Field label="server" value={server} />
        <Field label="blossom URI" value={chkBlossomUri(rootHash, rootKey, "bdir")} hint="The root manifest as a BUD-10 URI" />
        <Field label="root blob" value={blobUrl(server, toHex(rootHash))} hint="The raw ciphertext, straight from the server" />
        {event && <Field label="event id" value={event.id} />}
      </FieldGroup>

      {visibility !== "public" && (
        <SpecNote tone="warn">
          You chose a {visibility} root, so another client cannot open this tree from the mutable link alone
          {linkKey ? " unless it also has the k= parameter above" : ""}. The immutable <code>nhash</code> still carries
          the key directly.
        </SpecNote>
      )}

      <div className="space-y-3 rounded-lg border bg-card p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-sm font-medium">Prove it actually works</p>
            <p className="text-xs text-muted-foreground">
              Fetch every blob back from the server and rebuild your file from nothing but the root hash and root key.
            </p>
          </div>
          <button
            type="button"
            onClick={() => void verify()}
            disabled={verifying}
            className="inline-flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm font-medium transition-colors hover:bg-muted disabled:opacity-50"
          >
            {verifying ? <Loader2 className="size-4 animate-spin" /> : <ShieldCheck className="size-4" />}
            Verify
          </button>
        </div>

        {steps && (
          <div className="space-y-1.5">
            {steps.map((step, index) => (
              <div key={index} className="flex items-start gap-2 text-xs">
                {step.ok ? (
                  <Check className="mt-0.5 size-3.5 shrink-0 text-emerald-500" />
                ) : (
                  <AlertCircle className="mt-0.5 size-3.5 shrink-0 text-red-500" />
                )}
                <div>
                  <span>{step.label}</span>
                  {step.detail && <span className="ml-1.5 text-muted-foreground">{step.detail}</span>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <SpecNote>
        A reader that starts from the root hash checks every blob against the hash in the link that pointed at it. That
        is what makes the tree verifiable end to end: the server can withhold bytes, but it cannot substitute them.
      </SpecNote>

      <button
        type="button"
        onClick={onRestart}
        className="rounded-md border px-4 py-2 text-sm font-medium transition-colors hover:bg-muted"
      >
        Build another
      </button>
    </Slide>
  );
}

export { fromHex };
